import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, X, ChevronDown, Calendar, Clock, Users, Check, Zap, Coffee, Dumbbell, UtensilsCrossed } from 'lucide-react';
import api, { uploadMedia } from '../lib/api';
import ImageCropper from '../components/ImageCropper';
import LocationPicker from '../components/LocationPicker';
import VenuePartnerPicker from '../components/VenuePartnerPicker';
import RecurrencePicker from '../components/RecurrencePicker';

const CATEGORIES = [
  { value: 'coffee', label: 'Coffee' },
  { value: 'food', label: 'Food & Dining' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'sports', label: 'Sports' },
  { value: 'social', label: 'Social' },
  { value: 'networking', label: 'Networking' },
  { value: 'culture', label: 'Art & Culture' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'outdoors', label: 'Outdoors' },
];

const JOIN_MODES = [
  { value: 'open', label: 'Open Join', desc: 'Anyone can join instantly' },
  { value: 'request', label: 'Request to Join', desc: 'You approve who joins' },
  { value: 'queue', label: 'Auto Queue', desc: 'First come, first served' },
];

const QUICK_TEMPLATES = [
  { icon: Coffee, label: 'Coffee Now', title: 'Coffee Meetup', category: 'coffee', capacity: 6, time: 'now' },
  { icon: UtensilsCrossed, label: 'Dinner', title: 'Dinner Plans', category: 'food', capacity: 8, time: 'tonight' },
  { icon: Dumbbell, label: 'Workout', title: 'Group Workout', category: 'fitness', capacity: 12, time: 'tomorrow' },
  { icon: Zap, label: 'Hangout', title: 'Social Hangout', category: 'social', capacity: 10, time: 'now' },
];

const COST_TYPES = [
  { value: 'free', label: 'Free', emoji: '🆓', desc: 'No cost involved' },
  { value: 'individual', label: 'Pay individually', emoji: '🧾', desc: 'Each person pays their own' },
  { value: 'split', label: 'Split bill', emoji: '🤝', desc: 'Split the total evenly' },
  { value: 'fixed', label: 'Fixed amount', emoji: '💵', desc: 'Set price per person' },
  { value: 'host_pays', label: 'Host pays', emoji: '🎁', desc: 'On the host' },
  { value: 'unknown', label: 'Unknown', emoji: '❓', desc: 'Decided on the day' },
];

const COST_PRESETS = ['AED 30–50', 'AED 50–100', 'AED 100–200', 'AED 200–400', 'AED 400+', '$$$$'];

export default function CreatePlan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVibe = searchParams.get('from_vibe');
  const editId = searchParams.get('edit');
  const fromCommunityId = searchParams.get('community_id') || '';
  const isEdit = !!editId;

  const [form, setForm] = useState({
    title: fromVibe || '', description: '', category: 'social',
    city: '', venue: '', area: '', region: '',
    latitude: null, longitude: null,
    date: '', start_time: '', end_time: '',
    capacity: 10, join_mode: 'open', hashtags: '',
    cost_type: 'unknown', cost_amount: '',
    business_id: '',
    community_id: fromCommunityId,
    recurrence: null,
  });
  const [partnerBiz, setPartnerBiz] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [cropperSrc, setCropperSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(isEdit);
  const [error, setError] = useState('');

  // Hydrate from existing plan when editing
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/plans/${editId}`);
        if (cancelled) return;
        setForm({
          title: data.title || '',
          description: data.description || '',
          category: data.category || 'social',
          city: data.city || '',
          venue: data.venue || '',
          area: data.area || '',
          region: data.region || '',
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          date: data.date || '',
          start_time: data.start_time || '',
          end_time: data.end_time || '',
          capacity: data.capacity || 10,
          join_mode: data.join_mode || 'open',
          hashtags: (data.hashtags || []).join(', '),
          cost_type: data.cost_type || 'unknown',
          cost_amount: data.cost_amount || '',
          business_id: data.business_id || '',
          recurrence: data.recurrence || null,
        });
        if (data.business) setPartnerBiz(data.business);
        if (data.cover_image) setCoverPreview(data.cover_image);
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not load plan');
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEdit]);

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropperSrc(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleCoverCropSave = async (croppedFile) => {
    setCoverImage(croppedFile);
    setCoverPreview(URL.createObjectURL(croppedFile));
    setCropperSrc(null);
  };

  const handleQuickDate = (option) => {
    const now = new Date();
    if (option === 'today') {
      update('date', now.toISOString().split('T')[0]);
    } else if (option === 'tomorrow') {
      now.setDate(now.getDate() + 1);
      update('date', now.toISOString().split('T')[0]);
    } else if (option === 'weekend') {
      const daysUntilSat = (6 - now.getDay()) % 7 || 7;
      now.setDate(now.getDate() + daysUntilSat);
      update('date', now.toISOString().split('T')[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.start_time) {
      setError('Title, date, and start time are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let coverUrl = coverPreview && coverPreview.startsWith('http') ? coverPreview : '';
      if (coverImage) {
        coverUrl = (await uploadMedia(coverImage)).url;
      }

      const hashtags = form.hashtags
        .split(/[,\s#]+/)
        .filter(t => t.trim())
        .map(t => t.toLowerCase());

      const payload = {
        ...form,
        cover_image: coverUrl,
        hashtags,
        capacity: parseInt(form.capacity) || 10
      };

      if (isEdit) {
        await api.put(`/plans/${editId}`, payload);
        navigate(`/plan/${editId}`);
      } else {
        const { data } = await api.post('/plans', payload);
        navigate(`/plan/${data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || (isEdit ? 'Failed to save changes' : 'Failed to create plan'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-area animate-fade-in" data-testid="create-plan-page">
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-[#111111]" data-testid="create-plan-title">
          {isEdit ? 'Edit Plan' : 'Create Plan'}
        </h1>
        <button onClick={() => navigate(-1)} className="text-[#9B9EA3] text-sm">Cancel</button>
      </div>

      {hydrating && (
        <div className="px-4 py-12 text-center text-sm text-[#9B9EA3]" data-testid="edit-plan-hydrating">
          Loading plan…
        </div>
      )}

      {!hydrating && (
      <form onSubmit={handleSubmit} className="px-4 pb-8 space-y-5">
        {error && (
          <div data-testid="create-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-4 py-3 text-[#FF3B30] text-sm">
            {error}
          </div>
        )}

        {/* Quick Templates — only when creating, not editing */}
        {!isEdit && (
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Quick Start</label>
          <div className="grid grid-cols-4 gap-2">
            {QUICK_TEMPLATES.map(tmpl => {
              const Icon = tmpl.icon;
              return (
                <button
                  key={tmpl.label}
                  type="button"
                  data-testid={`template-${tmpl.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => {
                    update('title', tmpl.title);
                    update('category', tmpl.category);
                    update('capacity', tmpl.capacity);
                    handleQuickDate(tmpl.time === 'now' ? 'today' : tmpl.time === 'tonight' ? 'today' : 'tomorrow');
                    if (tmpl.time === 'now') {
                      const h = new Date().getHours();
                      update('start_time', `${String(h + 1).padStart(2, '0')}:00`);
                    } else if (tmpl.time === 'tonight') {
                      update('start_time', '20:00');
                    }
                  }}
                  className="flex flex-col items-center gap-1.5 p-3 bg-[#F1F0EA] rounded-2xl border border-black/[0.06] active:scale-95 transition-transform"
                >
                  <Icon size={20} className="text-[#B6E600]" />
                  <span className="text-[10px] font-medium text-[#6B6E73]">{tmpl.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Cover Image */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Cover Image</label>
          {coverPreview ? (
            <div className="relative rounded-2xl overflow-hidden">
              <img loading="lazy" decoding="async" src={coverPreview} alt="" className="w-full h-[180px] object-cover" />
              <button
                type="button"
                onClick={() => { setCoverImage(null); setCoverPreview(''); }}
                className="absolute top-2 right-2 bg-white/70 rounded-full p-1.5"
              >
                <X size={16} className="text-[#111111]" />
              </button>
            </div>
          ) : (
            <label className="upload-area flex flex-col items-center justify-center h-[140px] cursor-pointer" data-testid="cover-upload">
              <Camera size={28} className="text-[#9B9EA3] mb-2" />
              <span className="text-xs text-[#9B9EA3]">Tap to add cover photo</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
            </label>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Title</label>
          <input
            data-testid="plan-title"
            type="text"
            value={form.title}
            onChange={e => update('title', e.target.value)}
            placeholder="What's the plan?"
            required
            className="w-full bg-[#FFFFFF] text-[#111111] rounded-2xl px-4 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Description</label>
          <textarea
            data-testid="plan-description"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Tell people what to expect..."
            rows={3}
            className="w-full bg-[#FFFFFF] text-[#111111] rounded-2xl px-4 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8] resize-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Category</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                type="button"
                data-testid={`cat-${c.value}`}
                onClick={() => update('category', c.value)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                  form.category === c.value
                    ? 'bg-[#111111] text-white'
                    : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location — single smart search field with full UAE hierarchy */}
        <LocationPicker
          value={{
            venue: form.venue,
            area: form.area,
            region: form.region,
            city: form.city,
            latitude: form.latitude,
            longitude: form.longitude,
          }}
          onChange={(next) => setForm(f => ({ ...f, ...next }))}
        />

        {/* Partner venue (Q25) */}
        <VenuePartnerPicker
          city={form.city}
          planCategory={form.category}
          venueName={form.venue}
          value={{ business_id: form.business_id, business: partnerBiz }}
          onChange={({ business_id, business }) => {
            update('business_id', business_id);
            setPartnerBiz(business);
            // If picking a business, pre-fill the venue name + city when blank.
            if (business) {
              setForm(f => ({
                ...f,
                venue: f.venue || business.name,
                city: f.city || business.city,
              }));
            }
          }}
        />

        {/* Quick Date */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">When</label>
          <div className="flex gap-2 mb-3">
            {[['today', 'Today'], ['tomorrow', 'Tomorrow'], ['weekend', 'This Weekend']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`quick-${key}`}
                onClick={() => handleQuickDate(key)}
                className="px-4 py-1.5 rounded-full text-xs font-medium bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06] active:scale-95 transition-transform"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#B6E600]" />
                <input
                  data-testid="plan-date"
                  type="date"
                  value={form.date}
                  onChange={e => update('date', e.target.value)}
                  required
                  className="flex-1 bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 [color-scheme:dark]"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-3">
            <div className="flex-1">
              <label className="text-[10px] text-[#9B9EA3] uppercase tracking-wider mb-1 block">Start</label>
              <input
                data-testid="plan-start-time"
                type="time"
                value={form.start_time}
                onChange={e => update('start_time', e.target.value)}
                required
                className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 [color-scheme:dark]"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-[#9B9EA3] uppercase tracking-wider mb-1 block">End (optional)</label>
              <input
                data-testid="plan-end-time"
                type="time"
                value={form.end_time}
                onChange={e => update('end_time', e.target.value)}
                className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 [color-scheme:dark]"
              />
            </div>
          </div>
        </div>

        {/* Capacity */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">
            <Users size={14} className="inline mr-1" />Spots
          </label>
          <input
            data-testid="plan-capacity"
            type="number"
            min="2"
            max="200"
            value={form.capacity}
            onChange={e => update('capacity', e.target.value)}
            className="w-24 bg-[#FFFFFF] text-[#111111] text-center rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30"
          />
        </div>

        {/* Recurrence (Q29) */}
        <RecurrencePicker
          value={form.recurrence}
          seedDate={form.date}
          onChange={(rule) => update('recurrence', rule)}
        />

        {/* Join Mode */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Join Mode</label>
          <div className="space-y-2">
            {JOIN_MODES.map(mode => (
              <button
                key={mode.value}
                type="button"
                data-testid={`mode-${mode.value}`}
                onClick={() => update('join_mode', mode.value)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  form.join_mode === mode.value
                    ? 'bg-[#B6E600]/10 border-[#B6E600]/30'
                    : 'bg-[#FFFFFF] border-black/[0.06]'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  form.join_mode === mode.value ? 'border-[#B6E600]' : 'border-[#666]'
                }`}>
                  {form.join_mode === mode.value && <Check size={12} className="text-[#B6E600]" />}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-[#111111]">{mode.label}</p>
                  <p className="text-[11px] text-[#9B9EA3]">{mode.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Hashtags */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">Hashtags</label>
          <input
            data-testid="plan-hashtags"
            type="text"
            value={form.hashtags}
            onChange={e => update('hashtags', e.target.value)}
            placeholder="coffee, networking, social"
            className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
          />
        </div>

        {/* Cost Expectation */}
        <div>
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-2">
            Cost Expectation
          </label>
          <p className="text-[11px] text-[#9B9EA3] mb-2">
            Help guests know what to expect — ASAP doesn't handle payments.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {COST_TYPES.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => update('cost_type', opt.value)}
                data-testid={`cost-type-${opt.value}`}
                className={`flex flex-col items-start p-3 rounded-xl border transition-all ${
                  form.cost_type === opt.value
                    ? 'bg-[#B6E600]/10 border-[#B6E600]/45'
                    : 'bg-[#FFFFFF] border-black/[0.06]'
                }`}
              >
                <span className="text-base">{opt.emoji}</span>
                <span className="text-sm font-semibold text-[#111111] mt-1">{opt.label}</span>
                <span className="text-[11px] text-[#9B9EA3] mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>

          {form.cost_type !== 'free' && form.cost_type !== 'unknown' && (
            <div className="mt-3">
              <label className="block text-[11px] text-[#6B6E73] mb-1.5">
                Expected spend (optional)
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {COST_PRESETS.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => update('cost_amount', p)}
                    data-testid={`cost-preset-${p.replace(/\s/g, '_')}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      form.cost_amount === p ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#111]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <input
                data-testid="plan-cost-amount"
                type="text"
                value={form.cost_amount}
                onChange={e => update('cost_amount', e.target.value)}
                placeholder="e.g. AED 30–50, $$$$"
                maxLength={60}
                className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
              />
            </div>
          )}
        </div>

        {/* Submit */}
        <button
          data-testid="create-plan-submit"
          type="submit"
          disabled={loading}
          className="w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-3.5 text-sm active:scale-[0.98] transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(212,255,0,0.2)]"
        >
          {loading ? (isEdit ? 'Saving…' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Plan')}
        </button>
      </form>
      )}

      {/* Cover Image Cropper */}
      {cropperSrc && (
        <ImageCropper
          imageSrc={cropperSrc}
          onSave={handleCoverCropSave}
          onCancel={() => setCropperSrc(null)}
          aspect={16 / 9}
          circular={false}
        />
      )}
    </div>
  );
}
